import React, { memo, useMemo } from "react";

import {
  generateLinePath,
  getDirectionLineColor,
  getPriceDirection,
} from "~utils/ticker-chart";
import type { PricePoint, Scale } from "~types/types";

export interface SparkLineCellProps {
  data: PricePoint[];
  width: number;
  height: number;
  previousClose?: number;
  strokeWidth?: number;
  showBaseline?: boolean;
  baselineColor?: string;
  accessibilityLabel?: string;
}

function createLinearScale(
  domain: [number, number],
  range: [number, number]
): Scale {
  const [dMin, dMax] = domain;
  const [rMin, rMax] = range;
  const dSpan = dMax - dMin || 1;
  const rSpan = rMax - rMin;

  return {
    domain: () => domain,
    range: () => range,
    scale: (value: number) => rMin + ((value - dMin) / dSpan) * rSpan,
    invert: (pixel: number) => dMin + ((pixel - rMin) / rSpan) * dSpan,
  };
}

function SparkLineCellInner({
  data,
  width,
  height,
  previousClose,
  strokeWidth = 1.5,
  showBaseline = false,
  baselineColor = "#6b7280",
  accessibilityLabel,
}: SparkLineCellProps): React.ReactElement | null {
  const scales = useMemo(() => {
    if (data.length === 0) return null;

    const timestamps = data.map((p) => p.timestamp);
    const values = data.map((p) => p.value);

    const tMin = Math.min(...timestamps);
    const tMax = Math.max(...timestamps);
    let vMin = Math.min(...values);
    let vMax = Math.max(...values);

    // Expand y-domain to include previousClose so the baseline is always visible
    if (previousClose !== undefined) {
      vMin = Math.min(vMin, previousClose);
      vMax = Math.max(vMax, previousClose);
    }

    const xScale = createLinearScale([tMin, tMax], [0, width]);
    const yScale = createLinearScale([vMin, vMax], [height, 0]);

    return { xScale, yScale };
  }, [data, width, height, previousClose]);

  const linePath = useMemo(() => {
    if (!scales) return "";

    return generateLinePath(data, {
      xScale: scales.xScale,
      yScale: scales.yScale,
    });
  }, [data, scales]);

  const strokeColor = useMemo(() => {
    if (data.length < 2) return getDirectionLineColor("neutral");

    const firstPoint = data[0];
    const lastPoint = data[data.length - 1];

    if (!firstPoint || !lastPoint) return getDirectionLineColor("neutral");

    const baseValue = previousClose ?? firstPoint.value;
    const direction = getPriceDirection(baseValue, lastPoint.value);

    return getDirectionLineColor(direction);
  }, [data, previousClose]);

  const baselineY = useMemo(() => {
    if (!scales || previousClose === undefined) return null;

    return scales.yScale.scale(previousClose);
  }, [scales, previousClose]);

  if (data.length === 0) return null;

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={accessibilityLabel}
    >
      {showBaseline && baselineY !== null && (
        <line
          x1={0}
          y1={baselineY}
          x2={width}
          y2={baselineY}
          stroke={baselineColor}
          strokeWidth={1}
          strokeDasharray="2,2"
        />
      )}
      <path
        d={linePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const SparkLineCell = memo(SparkLineCellInner);
